import { React, useState, useEffect, useContext } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Heading, T } from "./helpful";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { LangContext } from "../context/context";
import { getHotelQuickInfo } from "../api/api";
import { useQuery } from "@tanstack/react-query";
import {QuickInfoSkeleton} from '../components/skeleton';


/**
 * The `HotelQuickInfo` function fetches and displays information about a specific hotel, including
 * features, images, and map location, based on the provided hotel ID and language.
 * @returns The `HotelQuickInfo` component is being returned. It displays information about a hotel
 * such as the hotel image, logo, tagline, features, total rooms, and restaurant details. The component
 * fetches data based on the `hotelID` and `lang` provided, and displays a loading skeleton while
 * fetching the data. It also includes a subheading function, a hotelFeatures function to display
 * features
 * @author lionel ghogue ngondeke <lionelladrog@gmail.com> 2024/10/11
 */
export default function HotelQuickInfo({ title }) {
  const { lang } = useContext(LangContext);
  const [hotel_features, setHotelFeatures] = useState(null);
  const [hotelTitle, setHotelTitle] = useState('');
  const [hotelTagline, setHotelTagline] = useState(null);
  const [hotelLogo, setHotelLogo] = useState(null);
  const [hotelImage, setHotelImage] = useState(null);
  const [hotelTotalRoom, setHotelTotalRoom] = useState(null);
  const [hotelRestaurant, setHotelRestaurant] = useState(null);
  const [map, setMap] = useState(null);
  const { t } = useTranslation(); 
  const location = useLocation();
  const pathArray = location.pathname.split("/");
  const hotelID = pathArray[2];

  const { data, error, isLoading } = useQuery({
    queryKey: [`hotel-${hotelID}-${lang}`, lang],
    queryFn: () => getHotelQuickInfo(lang, hotelID),
    enabled: !!hotelID, // Only run the query if hotelID is truthy
    staleTime: 1000 * 60 * 30,
    cacheTime: 1000 * 60 * 30,
    
  });

  const setStateIfExists = (setter, value) => {
    if (value !== undefined) {
      setter(value);
    }
  };
  useEffect(() => {
    if (data) {
    
      setStateIfExists(setHotelLogo, data.hotel_logo?.[0]?.imageDerivatives?.default_url);
      setStateIfExists(setMap, data.hotel_map?.[0]?.imageDerivatives?.default_url);
      setStateIfExists(setHotelFeatures, data.hotel_features);
      setStateIfExists(setHotelTitle, data.hotel_title);
      setStateIfExists(setHotelTagline, data.hotel_sub_title);
      setStateIfExists(setHotelImage, data.hotel_main_image?.[0]?.imageDerivatives?.default_url);
      setStateIfExists(setHotelTotalRoom, data.hotel_total_rooms);
      setStateIfExists(setHotelRestaurant, data.hotel_total_restaurants_bars);
    }
  }, [data]);

  
 
  const subHeading = () => {
    return <>{" "}</>;
  };

  const hotelFeatures = (features) => {
    let featureList = [];
    
    if (features && features.length > 0) {
      featureList = features.map((feature, index) => (
        <li key={index}>{feature + (index===0?` ${hotelTotalRoom}`: index===1?` ${hotelRestaurant}`:'')}</li>
      ));
    }
    return <>{featureList}</>;
  };


  return (
    <div>
      <div className="">
        <div>
          <Heading heading={title} Desc={subHeading} />
        </div>
        {isLoading || !data? <QuickInfoSkeleton/>: 
        <Row className="main-content">
        <Col sm="6">
          <div className="position-relative">
            <div >
              <img
                src={hotelImage}
                className="img-fluid hotel-img"
                alt="hotel_image"
              />
              <div className="d-flex justify-content-center">
              <img
                src={hotelLogo}
                className="hotel-logo "
                alt="hotel_logo"
              />
              </div>
            </div>

            <div className="hotel_tagline d-flex justify-content-center px-3">
              <h4 className="">{hotelTagline}</h4>
            </div>
          </div>
        </Col>
        <Col sm="6" className="">
          <div className="pt-3 pb-2 ">
            <p className="lead">
              <T value={"Hotel Features"} />
            </p>
            <div className="hotel-features ">
              <ul className="hotel_feature_list">
                {hotelFeatures(hotel_features)}
              </ul>
            </div>
          </div>
          <hr />
          <div>
            <img
              src={map}
              className="img-fluid mx-auto d-block map"
              alt="Hotel_map"
            />
          </div>
        </Col>
      </Row>
        }
        
      </div>
    </div>
  );
}
